<!--
  ReadinessBanner — read-only readiness signal for the product editor.

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 3)

  THIS BANNER IS INFORMATIONAL ONLY. It deliberately offers no action.

  Why (Jake, 2026-08-21): this surface used to carry a "Save to send N to
  digitizer" CTA. That was wrong on two counts. In principle, saving a
  placement and dispatching paid vendor work are different decisions with
  different consequences — once ArtisticPunch is live, sending costs real
  money — so they must not be coupled to one click. In practice the CTA
  was a stub with an empty handler: enabled whenever N > 0, so it looked
  live and silently did nothing.

  Digitization is triggered MANUALLY BY THE DISTRIBUTOR from the store
  editor's Decorations page. The backend batch-send endpoint
  (POST /api/campaign_products/<id>/send_pending_artworks) is called from
  there by the "Send pending artworks" button
  (store-editor/components/SendPendingArtworks.vue, ADR 0036), not from
  here. Do not re-add an actionable control to this banner.

  Behavior:
    - count = distinct bound artwork_file_id values across
      editor.decorations[*].variants[*].
    - Per P1-Q11 the real semantics is "bound but NOT in 'approved' state."
      Phase F migrations wire the real lifecycle state; for the interim
      build, treat all bound artworks as not-yet-approved (matches the
      Day 2 VariantCard placeholder chip).
    - Always visible (slim baseline). When N === 0 the label reads
      "No artwork bound yet" — distinct from "all approved", which is not
      yet reachable.
    - When N > 0 a hint names the Decorations page as where approvals are
      driven, without implying this page can drive them.
-->
<template>
    <footer
        data-testid="readiness-banner-root"
        class="readiness-banner"
        :data-count="count"
        :data-state="count > 0 ? 'pending' : 'clean'"
    >
        <div class="readiness-banner-text">
            <span class="readiness-banner-label" data-testid="readiness-banner-label">
                {{ labelText }}
            </span>
            <!--
              PR-L3 — routing coverage block. Surfaces "all colors routed"
              vs "K placements have N colors uncovered" using the variant
              resolution order shared with the backend (see
              decorationRouting.js). Only renders when the product has
              selected colors AND at least one decoration exists, so an
              empty product editor doesn't show a confusing chip.
            -->
            <span
                v-if="showRoutingBlock"
                data-testid="readiness-banner-routing"
                class="readiness-banner-routing"
                :data-state="routingState"
            >
                <span
                    class="material-icons readiness-banner-routing-mi"
                    aria-hidden="true"
                >{{ routingState === 'routed' ? 'check_circle' : 'warning' }}</span>
                {{ routingMessage }}
            </span>
        </div>
        <!--
          Informational hint only — a <span>, never a link or button.
          It names where approvals are driven (the store editor's
          Decorations page) so the user is not left guessing, while
          making clear this page does not act.
        -->
        <span
            v-if="count > 0"
            data-testid="readiness-banner-hint"
            class="readiness-banner-hint"
        >
            Approvals are managed on the store's Decorations page
        </span>
    </footer>
</template>

<script setup>
import { computed } from "vue";
import { useProductEditorStore } from "@/stores/productEditor";
import { uncoveredColorSkus } from "@/utils/decorationRouting";

const editor = useProductEditorStore();

const count = computed(() => {
    const seen = new Set();
    const decos = editor.decorations ?? [];
    for (const deco of decos) {
        for (const v of deco.variants ?? []) {
            if (v.artwork_file_id) seen.add(v.artwork_file_id);
        }
    }
    return seen.size;
});

// PR-L3 — kind-aware routing coverage.
//
// Mirrors DecorationInspector.selectedProductColors so the same
// {color_sku, name, hex} shape feeds the decoration routing helper.
// Returns [] when supplier meta has not loaded; the routing block
// gates on `productColors.length > 0` so we don't surface a false
// "uncovered" claim before the underlying data exists.
const productColors = computed(() => {
    const skus = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    const colors = editor.supplierMeta?.colors ?? [];
    const bySku = new Map(colors.map((c) => [c.color_sku, c]));
    return skus.map((sku) => {
        const row = bySku.get(sku);
        if (row) return { color_sku: sku, name: row.name ?? sku, hex: row.hex ?? null };
        return { color_sku: sku, name: sku, hex: null };
    });
});

// Per-decoration uncovered SKU count. A decoration is "blocked" when
// the resolver leaves at least one product color unrouted, matching
// the 404 path in api/src/endpoints/decoration_variants.py.
const decorationCoverage = computed(() => {
    const decos = editor.decorations ?? [];
    const out = [];
    for (const deco of decos) {
        const uncovered = uncoveredColorSkus(deco, productColors.value);
        out.push({ deco, uncoveredCount: uncovered.length });
    }
    return out;
});

const blockedDecorations = computed(
    () => decorationCoverage.value.filter((row) => row.uncoveredCount > 0),
);

const totalUncovered = computed(() => {
    let n = 0;
    for (const row of blockedDecorations.value) n += row.uncoveredCount;
    return n;
});

const showRoutingBlock = computed(() => {
    // Only render once colors AND at least one decoration are present.
    // An empty product editor shouldn't display a misleading routing
    // chip; nor should one where the supplier meta hasn't loaded yet
    // (productColors.length stays 0 until that join completes).
    if (productColors.value.length === 0) return false;
    if ((editor.decorations ?? []).length === 0) return false;
    return true;
});

const routingState = computed(
    () => (blockedDecorations.value.length === 0 ? "routed" : "blocked"),
);

const routingMessage = computed(() => {
    if (routingState.value === "routed") return "All colors routed";
    const placements = blockedDecorations.value.length;
    const uncovered = totalUncovered.value;
    const placementWord = placements === 1 ? "placement" : "placements";
    const colorWord = uncovered === 1 ? "color" : "colors";
    return (
        `${placements} ${placementWord} • `
        + `${uncovered} ${colorWord} uncovered`
    );
});

const labelText = computed(() => {
    // Audit gap #12: "All artworks approved" implied the user had already
    // shepherded artworks through an approval flow. When there's nothing
    // bound at all, that's misleading, so the zero case is stated as a
    // fact about binding rather than about approval. The "all bound + all
    // approved" case is not yet reachable (real lifecycle is Phase F).
    //
    // The copy is deliberately free of send/dispatch language: this
    // banner reports state, it does not offer an action.
    if (count.value === 0) return "No artwork bound yet";
    return `${count.value} ${count.value === 1 ? "artwork" : "artworks"} not yet approved`;
});
</script>

<style scoped>
.readiness-banner {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 24px;
    border-top: 1px solid var(--tf-border, #e0e0e0);
    background: var(--tf-surface, #fff);
}

.readiness-banner-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.readiness-banner-label {
    font-size: 14px;
    color: var(--tf-text-secondary, #555);
}

.readiness-banner-routing {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
}

.readiness-banner-routing[data-state="routed"] {
    color: var(--tf-success, #059669);
}

.readiness-banner-routing[data-state="blocked"] {
    color: var(--tf-warning, #b45309);
}

.readiness-banner-routing-mi {
    font-size: 14px !important;
}

/*
  Hint styling is deliberately unlike a control: secondary text color,
  no border, no background, no pointer cursor. It must never read as
  something the user can click.
*/
.readiness-banner-hint {
    flex: 0 0 auto;
    font-size: 12px;
    color: var(--tf-text-secondary, #555);
}
</style>
